// Plays a short animation over the page when a member adds a reaction. A few
// reactions get their own effect; every other reaction (and, later, custom
// emoji) gets the generic burst of its icon. Reduced motion gets a small pop.
//
// Kept free of app imports so it can be unit tested with `node --test`.

const EFFECTS = { love: 'hearts', yay: 'confetti', celebrate: 'confetti' }
const CONFETTI_COLORS = [
  '#f6c744',
  '#ef7b70',
  '#7cc6ff',
  '#8ce0b8',
  '#c9b6ff',
  '#ff8a5c',
]
const CENTER = 'translate(-50%, -50%)'

export const effectFor = (key, { reducedMotion = false } = {}) =>
  reducedMotion ? 'pop' : EFFECTS[key] || 'burst'

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const random = (min, max) => min + Math.random() * (max - min)
const moved = (dx, dy, rest = '') =>
  `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) ${rest}`

function piece(layer, x, y, { text, size, style = {} }) {
  const el = document.createElement('span')
  if (text) el.textContent = text
  Object.assign(el.style, {
    position: 'absolute',
    left: `${x}px`,
    top: `${y}px`,
    fontSize: `${size}px`,
    lineHeight: '1',
    transform: CENTER,
    ...style,
  })
  layer.appendChild(el)
  return el
}

function bigPop(layer, icon, x, y, size) {
  return piece(layer, x, y, {
    text: icon,
    size,
    style: { filter: 'drop-shadow(0 10px 22px rgba(0,0,0,.35))' },
  }).animate(
    [
      { transform: `${CENTER} scale(0) rotate(-15deg)`, opacity: 0 },
      {
        transform: `${CENTER} scale(1.3) rotate(6deg)`,
        opacity: 1,
        offset: 0.18,
      },
      { transform: `${CENTER} scale(.92) rotate(-2deg)`, offset: 0.32 },
      { transform: `${CENTER} scale(1)`, opacity: 1, offset: 0.78 },
      { transform: moved(0, -50, 'scale(.7)'), opacity: 0 },
    ],
    { duration: 1100, easing: 'cubic-bezier(.2,.9,.3,1)', fill: 'forwards' }
  )
}

const PLAYERS = {
  pop(layer, icon, x, y) {
    return [
      piece(layer, x, y, { text: icon, size: 40 }).animate(
        [
          { transform: `${CENTER} scale(.6)`, opacity: 0 },
          { transform: `${CENTER} scale(1.1)`, opacity: 1, offset: 0.4 },
          { transform: `${CENTER} scale(1)`, opacity: 0 },
        ],
        { duration: 500, fill: 'forwards' }
      ),
    ]
  },

  // Copies of the icon fly out in a ring around a big pop of it.
  burst(layer, icon, x, y) {
    const rays = Array.from({ length: 12 }, (_, i) => {
      const angle = (i / 12) * Math.PI * 2
      return piece(layer, x, y, { text: icon, size: 26 }).animate(
        [
          { transform: `${CENTER} scale(.2)`, opacity: 0 },
          { opacity: 1, offset: 0.15 },
          {
            transform: moved(
              Math.cos(angle) * 140,
              Math.sin(angle) * 140,
              'scale(1)'
            ),
            opacity: 0,
          },
        ],
        {
          duration: 900,
          delay: 60,
          easing: 'cubic-bezier(.15,.8,.3,1)',
          fill: 'forwards',
        }
      )
    })
    return [bigPop(layer, icon, x, y, 96), ...rays]
  },

  // Hearts float up the screen, swaying as they rise.
  hearts(layer, icon, x, y) {
    const rise = window.innerHeight * 0.7
    const hearts = Array.from({ length: 16 }, (_, i) => {
      const sway = (i % 2 ? -1 : 1) * random(15, 35)
      const startX = random(-140, 140)
      return piece(layer, x + startX, y, {
        text: i % 4 === 3 ? '💖' : icon,
        size: random(24, 48),
      }).animate(
        [
          { transform: `${CENTER} scale(.4)`, opacity: 0 },
          { opacity: 1, offset: 0.1 },
          { transform: moved(sway, -rise / 2, 'scale(1)'), offset: 0.5 },
          {
            transform: moved(-sway / 2, -rise - random(0, 200), 'scale(1.15)'),
            opacity: 0,
          },
        ],
        {
          duration: random(1600, 2100),
          delay: random(0, 500),
          easing: 'cubic-bezier(.3,.6,.4,1)',
          fill: 'forwards',
        }
      )
    })
    return [bigPop(layer, icon, x, y, 110), ...hearts]
  },

  // Confetti shoots up out of the reaction, then rains down.
  confetti(layer, icon, x, y) {
    const fall = window.innerHeight - y + 60
    const bits = Array.from({ length: 40 }, (_, i) => {
      const dx = random(-260, 260)
      const up = random(120, 320)
      const spin = random(360, 720) * (i % 2 ? 1 : -1)
      const el = piece(layer, x, y, {
        size: 0,
        style: {
          width: `${random(7, 12)}px`,
          height: `${random(12, 18)}px`,
          borderRadius: '2px',
          background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        },
      })
      return el.animate(
        [
          { transform: `${CENTER} rotate(0deg)`, opacity: 1 },
          {
            transform: moved(dx * 0.8, -up, `rotate(${spin / 3}deg)`),
            opacity: 1,
            offset: 0.3,
          },
          { transform: moved(dx, fall, `rotate(${spin}deg)`), opacity: 0 },
        ],
        {
          duration: random(1500, 2200),
          delay: random(0, 150),
          easing: 'cubic-bezier(.25,.6,.5,1)',
          fill: 'forwards',
        }
      )
    })
    return [bigPop(layer, icon, x, y, 110), ...bits]
  },
}

export function celebrateReaction({ key, icon, x, y }) {
  if (!icon || typeof document.body.animate !== 'function') return

  const layer = document.createElement('div')
  layer.setAttribute('aria-hidden', 'true')
  Object.assign(layer.style, {
    position: 'fixed',
    inset: '0',
    pointerEvents: 'none',
    zIndex: '2147483000',
    overflow: 'hidden',
  })
  document.body.appendChild(layer)

  const animations = PLAYERS[
    effectFor(key, { reducedMotion: prefersReducedMotion() })
  ](layer, icon, x, y)
  Promise.allSettled(animations.map((a) => a.finished)).then(() =>
    layer.remove()
  )
}
